<template>
    <div class="u-mt-20" style="padding: 20px 12px;">
        <h4>Frequência</h4>
        <p class="pa-sub">Faltas, percentual e frequência diária em cada aula.</p>

        <div v-if="loading" class="pa-loading"><img src="/public/img/loading-bar.gif" /></div>

        <template v-else>
            <el-alert v-if="erro" type="warning" :closable="false" show-icon :title="erro" />

            <template v-else>
                <!-- O backend declara o modo; o portal não decide nada disso. -->
                <el-alert v-if="dados.aviso" type="info" :closable="false" show-icon
                          :title="dados.aviso" class="u-mb-10" />

                <el-row :gutter="10" class="u-mb-10">
                    <el-col :xs="12" :sm="6">
                        <div class="kpi">
                            <div class="kpi-rotulo">Total de faltas</div>
                            <div class="kpi-valor">{{ mostrar(dados.total_faltas) }}</div>
                        </div>
                    </el-col>
                    <el-col :xs="12" :sm="6">
                        <div class="kpi">
                            <div class="kpi-rotulo">Faltas justificadas</div>
                            <div class="kpi-valor">{{ mostrar(dados.faltas_justificadas) }}</div>
                        </div>
                    </el-col>
                    <el-col :xs="12" :sm="6">
                        <div class="kpi">
                            <div class="kpi-rotulo">Frequência</div>
                            <!-- Indeterminado NUNCA vira 100%: mostra o traço e o motivo. -->
                            <div class="kpi-valor">{{ percentualTexto }}</div>
                        </div>
                    </el-col>
                    <el-col :xs="12" :sm="6">
                        <div class="kpi">
                            <div class="kpi-rotulo">Mínima exigida</div>
                            <div class="kpi-valor">{{ dados.frequencia_minima !== null && dados.frequencia_minima !== undefined ? dados.frequencia_minima + '%' : '—' }}</div>
                        </div>
                    </el-col>
                </el-row>

                <div v-if="dados.indeterminado" class="pa-vazio u-mb-10">
                    {{ dados.aviso || 'Ainda não é possível calcular a frequência.' }}
                </div>

                <!-- Gráfico por período: só quando o backend forneceu percentuais. -->
                <div v-if="temPorPeriodo" class="u-mb-10">
                    <h5>Frequência por período</h5>
                    <grafico-svg tipo="barra" titulo="Frequência por período"
                                 :rotulos="rotulosPeriodo" :series="seriePeriodo"
                                 :maximo="100" :referencia="dados.frequencia_minima || null"
                                 rotulo-referencia="mínima"
                                 mensagem-vazio="Ainda não há percentual calculado por período." />
                </div>

                <div v-if="dados.por_periodo && dados.por_periodo.length" class="u-mb-10">
                    <el-table :data="dados.por_periodo" size="mini" stripe style="width:100%">
                        <el-table-column label="Período" width="90">
                            <template slot-scope="s">{{ s.row.periodo }}º</template>
                        </el-table-column>
                        <el-table-column label="Faltas" width="90">
                            <template slot-scope="s">{{ mostrar(s.row.faltas) }}</template>
                        </el-table-column>
                        <el-table-column label="Aulas previstas" width="130" class-name="hidden-mobile">
                            <template slot-scope="s">{{ mostrar(s.row.aulas_dadas) }}</template>
                        </el-table-column>
                        <el-table-column label="Frequência" min-width="150">
                            <template slot-scope="s">
                                <span v-if="s.row.indeterminado" class="pa-menor">
                                    Indeterminado — {{ s.row.motivo }}
                                </span>
                                <el-tag v-else size="mini" :type="corSituacao(s.row.situacao)">
                                    {{ s.row.percentual_frequencia }}%
                                </el-tag>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <div v-if="dados.por_disciplina && dados.por_disciplina.length" class="u-mb-10">
                    <h5>Faltas por componente</h5>
                    <el-table :data="dados.por_disciplina" size="mini" stripe style="width:100%">
                        <el-table-column label="Componente" min-width="160">
                            <template slot-scope="s">{{ s.row.disciplina || '—' }}</template>
                        </el-table-column>
                        <el-table-column label="Faltas" width="90">
                            <template slot-scope="s">{{ mostrar(s.row.faltas) }}</template>
                        </el-table-column>
                    </el-table>
                </div>

                <div v-if="dados.diaria === null" class="pa-vazio">
                    Nesta turma as faltas são registradas apenas como total do período,
                    portanto não há detalhe por dia.
                </div>

                <div v-else-if="dados.diaria && dados.diaria.length" >
                    <h5>Frequência diária</h5>
                    <div v-for="(d, i) in dados.diaria" :key="'d' + i" class="dia-freq">
                        <div class="dia-freq-data">{{ formatarData(d.data) }}</div>
                        <el-tag v-for="(a, j) in d.aulas" :key="'a' + i + '-' + j" size="mini"
                                :type="corPresenca(a.situacao)" class="tag-aula">
                            {{ a.componente || 'Aula' }}: {{ rotuloPresenca(a.situacao) }}
                        </el-tag>
                    </div>
                </div>
            </template>
        </template>
    </div>
</template>

<script>
import GraficoSvg from '@/shared/grafico/grafico-svg.vue'

/**
 * BLOCO N — itens 14 (gráfico de frequência) e 15.
 *
 * Consome `api/portal/frequencia-detalhada`, que por sua vez usa o Bloco Q. Este
 * componente NÃO calcula percentual, não soma faltas e não decide situação: tudo vem
 * pronto. `null` é apresentado como "—", nunca como 0 ou 100%.
 */
export default {
    name: 'PortalFrequencia',
    components: { GraficoSvg },

    data: () => ({ loading: true, erro: null, dados: {} }),

    computed: {
        percentualTexto () {
            const p = this.dados.percentual_frequencia
            if (p === null || p === undefined || this.dados.indeterminado) return '—'
            return p + '%'
        },

        temPorPeriodo () {
            const lista = this.dados.por_periodo || []
            return lista.some(p => p.percentual_frequencia !== null && p.percentual_frequencia !== undefined)
        },

        rotulosPeriodo () {
            return (this.dados.por_periodo || []).map(p => p.periodo + 'º')
        },

        seriePeriodo () {
            // Valor null permanece null: o gráfico deixa lacuna em vez de plotar zero.
            return [{
                nome: 'Frequência',
                valores: (this.dados.por_periodo || []).map(p =>
                    (p.indeterminado || p.percentual_frequencia === undefined) ? null : p.percentual_frequencia)
            }]
        }
    },

    mounted () { this.carregar() },

    methods: {
        carregar () {
            this.loading = true
            this.erro = null

            this.$axios.post(`${this.$store.state.config.baseUrlApi}/api/portal/frequencia-detalhada`,
                             {}, this.$store.getters.getHeaderAuth).then(r => {

                this.loading = false
                const d = r.data || {}
                if (d.erro) { this.erro = d.erro; return }

                this.dados = d

            }).catch(() => {
                this.loading = false
                this.erro = 'Não foi possível carregar sua frequência.'
            })
        },

        /** Ausência de dado é traço — nunca zero. */
        mostrar (v) {
            return (v === null || v === undefined || v === '') ? '—' : v
        },

        formatarData (d) {
            if (!d) return '—'
            const p = String(d).substring(0, 10).split('-')
            return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d
        },

        rotuloPresenca (v) {
            return { presente: 'Presente', falta: 'Falta', falta_justificada: 'Justificada',
                     sem_lancamento: 'Sem lançamento' }[v] || '—'
        },

        corPresenca (v) {
            return { presente: 'success', falta: 'danger', falta_justificada: 'warning',
                     sem_lancamento: 'info' }[v] || 'info'
        },

        corSituacao (s) {
            return { regular: 'success', atencao: 'warning', critica: 'danger' }[s] || 'info'
        }
    }
}
</script>

<style scoped>
.pa-loading { min-height: 300px; display: flex; justify-content: center; align-items: center; }
.pa-sub { color: #909399; }
.pa-menor { color: #909399; font-size: 12px; }
.pa-vazio { padding: 20px 12px; text-align: center; color: #909399; background: #fafafa; border-radius: 4px; }
.kpi { background: #fafafa; border-radius: 4px; padding: 10px; text-align: center; margin-bottom: 8px; }
.kpi-rotulo { font-size: 11px; color: #909399; }
.kpi-valor { font-size: 18px; font-weight: 600; color: #303133; }
.dia-freq { border-bottom: 1px solid #f0f2f5; padding: 8px 0; }
.dia-freq-data { font-weight: 600; font-size: 13px; margin-bottom: 4px; }
.tag-aula { margin: 2px 4px 2px 0; }
@media (max-width: 640px) { .hidden-mobile { display: none !important; } }
</style>
